import React, {useEffect, useState} from 'react'
import {Button, Card, Col, DatePicker, Descriptions, Divider, Form, InputNumber, Modal, Row, Table, Tag} from 'antd'
import SweetAlertService from '../../../services/lib/utils/sweet-alert-service'
import productStickersPdfGenerate from '../../static/pdf/product-stickers/product-stickers-pdf-generate_all'
import palletSticker from '../../static/pdf/pallet-stickers/pallet-sticker'
import {ApiRequest} from '../../../services/api/api'
import {commonFormSelect, getSelectOptions} from '../../../components/common/common-form/common-form-select'
import {urlKey} from '../../../services/api/api-urls'
import {getUserId} from '../../../services/lib/utils/auth.utils'
import {JadCupImg, emptyImg} from '../../../services/others/assets'
import {IStickerPdf} from '../../../services/model/IStickerPdf'
import getImage from '../../../components/common/common-form/common-image'
import moment from 'moment'

interface AutoPrintBarcodeModalProps {
  visible: any
  onOk: any
  onCancel: any
}

interface ResultPalletInfo {
  plateId: any
  plateCode: string
  boxCount: number
  quantity: number
}

const DEFAULT_BOXES_PER_PALLET = 100
const MAX_PALLETS_PER_REQUEST = 20

/**
 * 自动化 Print Barcode:
 * 选产品 + 输入数量(默认包装数量/默认生产日期) -> 确认后自动:
 * 1. 生成产品箱子(Box/GenerateBox)
 * 2. 自动创建托盘(Plate/AddTemporaryPallets, 按产品堆叠规则计算托盘数)
 * 3. 自动把箱子放入托盘(PlateBox/UpdateWareshouePalletBox)
 * 4. 自动打印箱子条码标签(可补打托盘标签)
 */
export const AutoPrintBarcodeModal = (props: AutoPrintBarcodeModalProps) => {
  const {onCancel, onOk} = props
  const [isVisible, setIsVisible] = useState(false)
  const [productsOptions, setProductsOptions] = useState([])
  const [selectedProduct, setSelectedProduct] = useState<any>()
  const [qtyPerPack, setQtyPerPack] = useState<any>()
  const [boxCount, setBoxCount] = useState<any>()
  const [barcodeDate, setBarcodeDate] = useState<moment.Moment>(moment())
  const [boxesPerPallet, setBoxesPerPallet] = useState<number>(DEFAULT_BOXES_PER_PALLET)
  const [isRunning, setIsRunning] = useState(false)
  const [resultPallets, setResultPallets] = useState<ResultPalletInfo[]>([])
  const [resultBoxes, setResultBoxes] = useState<any[]>([])
  const [selectedBarcodes, setSelectedBarcodes] = useState<string[]>([])
  const [base64, setBase64] = useState(String)
  const [productData, setProductData] = useState<any>()
  const [productDetail, setProductDetail] = useState<any>()
  const [resBarcodeDate, setResBarcodeDate] = useState<any>()

  useEffect(() => {
    setIsVisible(props.visible)
  }, [props.visible])

  useEffect(() => {
    getSelectOptions('', 'Product/GetAllProduct?withOutStock=true').then(res => setProductsOptions(res))
  }, [])

  const onProductChange = async (value: any) => {
    setSelectedProduct(value)
    setResultPallets([])
    setResultBoxes([])
    setSelectedBarcodes([])
    setProductDetail(null)
    const foundProduct: any = productsOptions.filter((e: any) => e.productId == value)[0]
    // 默认包装数量
    setQtyPerPack(foundProduct?.baseProduct?.packagingType?.quantity || null)
    // 默认托盘堆叠规则(每托箱数)
    try {
      const productResponse = await ApiRequest({
        url: 'Product/GetProductById?id=' + value,
        method: 'get',
        isShowSpinner: false
      })
      setProductDetail(productResponse?.data?.data)
      const stackingQty = productResponse?.data?.data?.palletStacking?.quantity || DEFAULT_BOXES_PER_PALLET
      setBoxesPerPallet(stackingQty)
    } catch (error) {
      setBoxesPerPallet(DEFAULT_BOXES_PER_PALLET)
    }
  }

  const palletCountPreview = boxCount ? Math.ceil(boxCount / boxesPerPallet) : 0

  // ---------- 以下标签打印逻辑与现有 Print Barcode 对话框保持一致 ----------
  // logo 直接解析并返回(避免依赖 state 闭包导致 undefined)
  const resolveLogoBase64 = async (logoType: any, logoUrl: any): Promise<string> => {
    if (logoType == 2) {
      return emptyImg
    }
    if (logoType == 1) {
      if (!logoUrl) {
        SweetAlertService.errorMessage('Please configure the label image on the product setup page.')
        return JadCupImg
      }
      const imgUrl = logoUrl?.split('---')[0] ? JSON.parse(logoUrl?.split('---')[0])?.url : ''
      const res = await ApiRequest({
        url: 'Common/UrltoBase64?imageFile=' + imgUrl,
        method: 'get'
      })
      return 'data:image/png;base64,' + res.data
    }
    return JadCupImg
  }

  const stickerDetailInPdf = (barcodes: string[], product: any, logoBase64: string, manufactureDate: any): IStickerPdf[] => {
    const packaging = product?.baseProduct?.packagingType?.sleevePkt + 'pcs X '
      + product?.baseProduct?.packagingType?.sleeveQty + 'pk'
    return barcodes.map(barcode => ({
      barcode: barcode,
      productName: product?.productName || '',
      logoType: product?.logoType,
      logoUrl: product?.logoUrl,
      logoBase64: logoBase64,
      productCode: product?.productCode || '',
      custProductCode: product?.custProductCode || '',
      packaging: packaging,
      sleevePkt: product?.baseProduct?.packagingType?.sleevePkt,
      madeInNz: (product?.logoType !== 3) ? 'Made In New Zealand' : '',
      testLogo: JadCupImg,
      manufactureDate: manufactureDate,
      quantity: product?.baseProduct?.packagingType?.quantity,
      batchNumber: '',
      grossWeigh: product?.baseProduct?.grossWeigh || '',
      cartonLength: product?.baseProduct?.cartonLength,
      cartonWidth: product?.baseProduct?.cartonWidth,
      cartonHeight: product?.baseProduct?.cartonHeight
    }))
  }

  const printBoxStickers = async (barcodes?: string[]) => {
    if ((!resultBoxes.length && !barcodes?.length) || !productData) return
    const barcodesToPrint = barcodes?.length ? barcodes
      : (selectedBarcodes.length ? selectedBarcodes : resultBoxes.map((row: any) => row.barCode))
    const stickers = stickerDetailInPdf(barcodesToPrint, productData, base64, resBarcodeDate)
    await productStickersPdfGenerate(stickers, 'print')
  }

  const printPalletStickers = async () => {
    if (!resultPallets.length) return
    await palletSticker(resultPallets.map((row: any) => row.plateCode), 'print')
  }
  // ---------- 标签打印逻辑结束 ----------

  const generateBoxes = async () => {
    const res = await ApiRequest({
      url: 'Box/GenerateBox?productId=' + selectedProduct + '&QtyPerPack=' + qtyPerPack
        + '&boxQty=' + boxCount + '&barcodeDate=' + barcodeDate.format('YYYY-MM-DDTHH:mm:ss')
        + '&createdBy=' + getUserId(),
      method: 'post',
      isShowSpinner: true
    })
    const boxes = res.data.data
    boxes.sort((a: any, b: any) => a.sequence - b.sequence)
    return boxes
  }

  const createPallets = async (palletQty: number) => {
    // AddTemporaryPallets 单次最多 20 个,超出自动分批
    const createdPlateCodes: string[] = []
    let remaining = palletQty
    while (remaining > 0) {
      const batchQty = Math.min(MAX_PALLETS_PER_REQUEST, remaining)
      const res = await ApiRequest({
        url: 'Plate/AddTemporaryPallets?plateTypeId=3&packagingFlag=0&qty=' + batchQty,
        method: 'post',
        isShowSpinner: true
      })
      createdPlateCodes.push(...res.data.data)
      remaining -= batchQty
    }
    // 通过编码查询托盘 Id(新建托盘会出现在入库托盘列表中)
    const platesRes = await ApiRequest({
      url: 'Plate/GetInWarehouse',
      method: 'get',
      isShowSpinner: true
    })
    const plateMap = new Map<string, any>()
    platesRes.data.data.forEach((plate: any) => plateMap.set(plate.plateCode, plate))
    const pallets = createdPlateCodes.map((plateCode: string) => ({
      plateId: plateMap.get(plateCode)?.plateId,
      plateCode: plateCode
    }))
    if (pallets.some((pallet: any) => !pallet.plateId)) {
      throw new Error('Cannot resolve newly created pallet ids.')
    }
    return pallets
  }

  const onConfirm = async () => {
    if (!selectedProduct || !boxCount || !qtyPerPack) {
      SweetAlertService.errorMessage('Please select Product and input Count.')
      return
    }
    if (boxCount < 1 || boxCount > 500) {
      SweetAlertService.errorMessage('Box count must be between 1 and 500.')
      return
    }
    const palletQty = Math.ceil(boxCount / boxesPerPallet)
    const confirmResult = await SweetAlertService.confirmMessage(
      'Auto generate ' + boxCount + ' boxes and create ' + palletQty + ' pallet(s)?'
    )
    if (!confirmResult) return

    setIsRunning(true)
    setResultPallets([])
    setResultBoxes([])
    try {
      // 1. 自动生成产品箱子
      const boxes = await generateBoxes()
      const product = boxes[0]?.product
      if (!product) {
        throw new Error('Product data is missing in the generated boxes.')
      }
      const barcodeCreatedAt = boxes[0].createdAt
      setProductData(product)
      setResBarcodeDate(barcodeCreatedAt)

      // 2. 自动创建托盘
      const pallets = await createPallets(palletQty)

      // 3. 自动把箱子放入托盘(按堆叠规则顺序装托)
      const plateBox = boxes.map((box: any, index: number) => ({
        boxId: box.boxId,
        barCode: box.barCode,
        quantity: box.quantity,
        plateId: pallets[Math.floor(index / boxesPerPallet)].plateId
      }))
      await ApiRequest({
        url: 'PlateBox/UpdateWareshouePalletBox?employeeId=' + getUserId(),
        method: 'put',
        data: plateBox,
        isShowSpinner: true
      })

      const palletSummary: ResultPalletInfo[] = pallets.map((pallet: any) => {
        const palletBoxes = plateBox.filter((row: any) => row.plateId === pallet.plateId)
        return {
          plateId: pallet.plateId,
          plateCode: pallet.plateCode,
          boxCount: palletBoxes.length,
          quantity: palletBoxes.reduce((a: number, row: any) => a + row.quantity, 0)
        }
      })
      // 生成的箱子列表(含所属托盘),用于结果区展示与勾选补打
      const boxList = boxes.map((box: any, index: number) => ({
        ...box,
        plateCode: pallets[Math.floor(index / boxesPerPallet)].plateCode
      }))
      setResultPallets(palletSummary)
      setResultBoxes(boxList)
      setSelectedBarcodes(boxList.map((row: any) => row.barCode))
      // 4. 自动打印箱子条码标签(显式传入本次数据,await logo 解析,避免闭包读取到旧 state)
      const logoBase64 = await resolveLogoBase64(product.logoType, product.logoUrl)
      setBase64(logoBase64)
      await SweetAlertService.successMessage(
        'Success! ' + boxes.length + ' boxes generated and placed into ' + palletSummary.length + ' pallet(s).'
      )
      const stickers = stickerDetailInPdf(boxList.map((row: any) => row.barCode), product, logoBase64, barcodeCreatedAt)
      await productStickersPdfGenerate(stickers, 'print')
    } catch (error: any) {
      console.error(error)
      SweetAlertService.errorMessage('Auto Print Barcode failed.', error?.message || '')
    } finally {
      setIsRunning(false)
    }
  }

  const onModalClose = () => {
    if (resultPallets.length) {
      onOk()
    }
    onCancel()
  }

  return (
    <Modal
      destroyOnClose={true}
      title={
        <span>
          <span style={{marginRight: 8}}>📦</span>
          Manual Create Product
        </span>
      }
      visible={isVisible}
      onCancel={onModalClose}
      width={960}
      footer={false}
    >
      {/* ---------- 第一行:必填项 产品 + 箱数 ---------- */}
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={14}>
            <Form.Item required={true} label="Product" style={{marginBottom: 8}}>
              {commonFormSelect(urlKey.Product, productsOptions, ['productCode', 'productName'], false,
                (value: any) => onProductChange(value))}
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item required={true} label="Box Count" style={{marginBottom: 8}}>
              <InputNumber style={{width: '100%'}} value={boxCount} min={1} max={500}
                           placeholder="1 - 500"
                           onChange={(value: any) => setBoxCount(value)}/>
            </Form.Item>
          </Col>
        </Row>
        {/* ---------- 第二行:默认值 每箱数量 + 生产日期 ---------- */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={<span style={{color: '#666'}}>Qty Per Package<span style={{marginLeft: 4}}>(Default)</span></span>}
                       style={{marginBottom: 8}}>
              <InputNumber style={{width: '100%'}} value={qtyPerPack} readOnly/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={<span style={{color: '#666'}}>Production Date<span style={{marginLeft: 4}}>(Default: Today)</span></span>}
                       style={{marginBottom: 8}}>
              <DatePicker style={{width: '100%'}} value={barcodeDate}
                          onChange={(value: any) => setBarcodeDate(value)}/>
            </Form.Item>
          </Col>
        </Row>
        {selectedProduct && (
          <div style={{marginBottom: 8}}>
            <Tag color="blue">Pallets: {palletCountPreview}</Tag>
            <Tag color="green">{boxesPerPallet} boxes / pallet (stacking rule)</Tag>
            {boxCount && <Tag color="orange">Total Qty: {boxCount * (qtyPerPack || 0)}</Tag>}
          </div>
        )}
      </Form>

      {/* ---------- 产品信息卡(选产品后显示) ---------- */}
      {productDetail && (
        <Card size="small" style={{marginBottom: 12, background: '#fafafa'}} bodyStyle={{padding: 12}}>
          <Row gutter={16}>
            <Col span={6} style={{textAlign: 'center', minHeight: 100}}>
              {getImage(productDetail?.images) || <span style={{color: '#999'}}>No Image</span>}
            </Col>
            <Col span={18}>
              <Descriptions size="small" column={2} labelStyle={{color: '#666', width: 110}}>
                <Descriptions.Item label="Code">{productDetail.productCode}</Descriptions.Item>
                <Descriptions.Item label="Cust Code">{productDetail.custProductCode || '-'}</Descriptions.Item>
                <Descriptions.Item label="Name" span={2}>{productDetail.productName}</Descriptions.Item>
                <Descriptions.Item label="Pack / Box">
                  {productDetail?.baseProduct?.packagingType?.packagingTypeName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Qty / Pack">
                  {productDetail?.baseProduct?.packagingType?.quantity ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Pallet Stacking" span={2}>
                  {productDetail?.palletStacking?.palletStackingName || '-'}
                  {productDetail?.palletStacking && (
                    <span style={{marginLeft: 8, color: '#888', fontSize: 12}}>
                      ({productDetail.palletStacking.ctnsPerLevel || 0} × {productDetail.palletStacking.level || 0}
                      {(productDetail.palletStacking.addOnQty || 0) > 0 ? ' + ' + productDetail.palletStacking.addOnQty : ''}
                      = {productDetail.palletStacking.quantity} boxes)
                    </span>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Card>
      )}

      {/* ---------- 操作说明(有结果后隐藏) ---------- */}
      {resultPallets.length === 0 && (
        <Card size="small" style={{marginBottom: 4, background: '#f0f7ff', border: '1px dashed #adc6ff'}} bodyStyle={{padding: '8px 12px'}}>
          <div style={{fontWeight: 600, marginBottom: 4}}>📘 How to use</div>
          <div style={{color: '#555', fontSize: 13, lineHeight: '22px'}}>
            ① Select product — packaging qty, pallet stacking rule and product info are auto-filled<br/>
            ② Input box count (1–500) and check the pallet preview<br/>
            ③ Click Confirm — auto: generate boxes → create pallets → load boxes into pallets → print stickers
          </div>
        </Card>
      )}
      <Divider style={{margin: '12px 0'}}/>
      <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem'}}>
        <Button onClick={onModalClose} style={{marginRight: '2rem'}} disabled={isRunning}>Cancel</Button>
        <Button type="primary" size="large" onClick={onConfirm} loading={isRunning}
                disabled={!selectedProduct || !boxCount || !qtyPerPack}>Confirm</Button>
      </div>
      {resultPallets.length > 0 && (
        <div style={{marginTop: '0.5rem'}}>
          <Divider style={{margin: '12px 0'}}/>
          <div style={{fontWeight: 600, margin: '0.5rem 0', color: '#389e0d'}}>✅ Result — Pallets</div>
          <Table
            rowKey={(row: any) => row.plateId + ''}
            dataSource={resultPallets}
            pagination={false}
            size="small"
            columns={[
              {title: 'Pallet Code', dataIndex: 'plateCode'},
              {title: 'Box Count', dataIndex: 'boxCount'},
              {title: 'Total Quantity', dataIndex: 'quantity'}
            ]}
          />
          <div style={{fontWeight: 600, margin: '1rem 0 0.5rem'}}>
            🏷 Generated Barcodes ({resultBoxes.length})
          </div>
          <div style={{marginBottom: '0.5rem'}}>
            <a onClick={() => setSelectedBarcodes(resultBoxes.map((row: any) => row.barCode))}>Select All</a>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <a onClick={() => setSelectedBarcodes([])}>Clear</a>
            <span style={{marginLeft: '1rem', color: '#888'}}>Selected: {selectedBarcodes.length}</span>
          </div>
          <Table
            rowKey={(row: any) => row.barCode}
            dataSource={resultBoxes}
            pagination={{pageSize: 20, showSizeChanger: false}}
            size="small"
            rowSelection={{
              selectedRowKeys: selectedBarcodes,
              onChange: (selectedRowKeys: any) => setSelectedBarcodes(selectedRowKeys as string[])
            }}
            columns={[
              {title: '#', dataIndex: 'sequence', width: 60},
              {title: 'Barcode', dataIndex: 'barCode'},
              {title: 'Pallet', dataIndex: 'plateCode'},
              {title: 'Qty', dataIndex: 'quantity', width: 80}
            ]}
          />
          <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '1rem'}}>
            <Button onClick={printPalletStickers} style={{marginRight: '1rem'}}>Print Pallet Stickers</Button>
            <Button type="primary" onClick={() => printBoxStickers()} disabled={!selectedBarcodes.length}>
              Print Selected Stickers
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
